import { observer } from 'mobx-react';
import { action, computed, observable } from 'mobx';
import {
    arrayMove,
    autoinject,
    bind,
    componentAutoReaction,
    componentReaction,
    deepStringFind,
    Enums,
    inject,
    TableHeadOptions,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { AccountStore } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { cellIdToString, IDisplayCellMeta } from '../Displays/DisplayCells';

export interface IDisplayCellOffset {
    id: string | Mri.WebApi.MeasurementId;
    offset: number;
    width: number;
}

interface IDisplayTableHeaderProps {
    readonly cell: IDisplayCellMeta;
    readonly vm: DisplayListViewModel;
    readonly rowRef: HTMLTableRowElement | null;
}

@autoinject
@observer
export class DisplayTableHeader extends React.Component<IDisplayTableHeaderProps> {
    public render() {
        const { id, field, afterHeaderRender, shortHeaderLabel } = this.props.cell;
        const { sort, onSortChange } = this.props.vm;
        const header =
            typeof id == 'number' && Enums.BriefMeasurementId.has(id)
                ? Enums.toString(Enums.BriefMeasurementId, id)
                : id;
        const cellIdString = cellIdToString(id);
        const sortField = field != null ? field : typeof id == 'number' ? id.toString() : undefined;

        return (
            <>
                {this.isSticky && (
                    <DisplayStickyTableHeader
                        rowRef={this.props.rowRef}
                        cell={this.props.cell}
                        vm={this.props.vm}
                        isHighlighted={this.isHighlighted}
                    />
                )}
                {!this.isSticky && (
                    <th id={cellIdString} ref={this.bindHeaderRef} className={this.headerClass}>
                        {afterHeaderRender && afterHeaderRender(this.props.vm)}

                        <TableHeadOptions
                            value={sort}
                            field={sortField}
                            onChange={this.onSortChange}
                            showStickyHeaders={this.props.vm.showStickyColumns}
                            onStickyChange={this.onStickyColumnChange}
                            exclusive={this.accountStore.user?.multiSortPreferred != true}
                        >
                            {shortHeaderLabel ?? header}
                        </TableHeadOptions>
                    </th>
                )}
            </>
        );
    }

    @bind
    @action
    private onSortChange(sort: string[]) {
        this.props.vm.onSortChange(sort);
        if (sort.length > 0) {
            this.appStore.storage.sortedColumn = cellIdToString(this.props.cell.id);
        } else {
            this.appStore.storage.sortedColumn = '';
        }
    }

    @bind
    @action
    private bindHeaderRef(ref: HTMLTableCellElement | null) {
        this.headerRef = ref;
    }

    @bind
    @action
    private onStickyColumnChange() {
        if (this.props.rowRef && this.headerRef) {
            const { id } = this.props.cell;
            const stickyColumn = cellIdToString(id);

            this.props.vm.onStickyColumnChange(stickyColumn);
        }
    }

    @computed
    private get isSticky() {
        const { id } = this.props.cell;
        const stickyColumn = cellIdToString(id);
        if (!this.props.vm.showStickyColumns) return false;

        return this.props.vm.stickyColumns.includes(stickyColumn);
    }

    @computed
    private get isHighlighted() {
        const { cell } = this.props;
        const { searchedColumn } = this.props.vm;
        const cellString = cellIdToString(cell.id);

        if (searchedColumn.length > 0 && deepStringFind(cellString, searchedColumn)) {
            return true;
        }
        return false;
    }

    @computed
    private get headerClass() {
        let classname = 'text-center display-table-header';

        if (this.isSorted) {
            classname = classname + ' sorted-header';
        }
        if (this.isHighlighted) {
            classname = classname + ' highlighted-header';
        }
        if (!this.accountStore.storage.wrappedHeaders) {
            classname = classname + ' no-wrap';
        }
        return classname;
    }

    @computed
    private get isSorted() {
        const { cell } = this.props;
        return this.appStore.storage.sortedColumn == cellIdToString(cell.id);
    }

    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
    private headerRef: HTMLTableCellElement | null = null;
}

export interface IDisplayStickyTableHeaderProps {
    readonly cell: IDisplayCellMeta;
    readonly vm: DisplayListViewModel;
    readonly rowRef: HTMLTableRowElement | null;
    readonly isHighlighted: boolean;
}

@autoinject
@observer
export class DisplayStickyTableHeader extends React.Component<IDisplayStickyTableHeaderProps> {
    public constructor(props: IDisplayStickyTableHeaderProps) {
        super(props);
        //5k timeouts
        componentAutoReaction(
            this,
            () => this.props.vm.stickyColumns.length,
            () => {
                this.shortTimer = window.setTimeout(() => this.updateStickyOffset(), 2000);
                this.timer = window.setTimeout(() => this.updateStickyOffset(), 5000);
            }
        );
        componentAutoReaction(
            this,
            () => this.props.vm.droppedHeaders.length,
            () => {
                this.shortTimer = window.setTimeout(() => this.updateStickyOffset(), 2000);
                this.timer = window.setTimeout(() => this.updateStickyOffset(), 5000);
            }
        );
        componentAutoReaction(
            this,
            () => this.props.vm.columns.length,
            () => {
                this.shortTimer = window.setTimeout(() => this.updateStickyOffset(), 2000);
                this.timer = window.setTimeout(() => this.updateStickyOffset(), 5000);
            }
        );
        componentAutoReaction(
            this,
            () => this.props.vm.configColumns.length,
            () => {
                this.shortTimer = window.setTimeout(() => this.updateStickyOffset(), 2000);
                this.timer = window.setTimeout(() => this.updateStickyOffset(), 5000);
            }
        );
        componentAutoReaction(
            this,
            () => this.props.vm.viewType,
            () => {
                this.shortTimer = window.setTimeout(() => this.updateStickyOffset(), 2000);
                this.timer = window.setTimeout(() => this.updateStickyOffset(), 5000);
            }
        );
    }

    @action
    public componentDidMount() {
        document.addEventListener('onmouseup', e => this.closeDragElement);
        this.updateStickyOffset();
        this.timer = window.setTimeout(() => this.updateStickyOffset(), 5000);
    }

    @action
    public componentWillUnmount() {
        if (this.timer) window.clearTimeout(this.timer);
        if (this.shortTimer) window.clearTimeout(this.shortTimer);
    }

    public render() {
        const { id, field, afterHeaderRender, shortHeaderLabel } = this.props.cell;
        const { sort } = this.props.vm;
        const header =
            typeof id == 'number' && Enums.BriefMeasurementId.has(id)
                ? Enums.toString(Enums.BriefMeasurementId, id)
                : id;
        const cellIdString = cellIdToString(id);
        const sortField = field != null ? field : typeof id == 'number' ? id.toString() : undefined;

        return (
            <th
                id={cellIdString}
                className={this.className + ' text-center'}
                ref={this.bindHeaderRef}
                style={this.isSticky ? this.stickyStyle : undefined}
                onMouseDown={this.dragMouseDown}
            >
                {afterHeaderRender && afterHeaderRender(this.props.vm)}

                <TableHeadOptions
                    value={sort}
                    field={sortField}
                    onChange={this.onSortChange}
                    showStickyHeaders={this.props.vm.showStickyColumns}
                    onStickyChange={this.onStickyColumnChange}
                    exclusive={this.accountStore.user?.multiSortPreferred != true}
                >
                    {shortHeaderLabel ?? header}
                </TableHeadOptions>
            </th>
        );
    }

    @bind
    @action
    private dragMouseDown(e: React.MouseEvent) {
        e.preventDefault();
        // get the mouse cursor position at startup:
        document.onmouseup = this.closeDragElement as any;
        // call a function whenever the cursor moves:
        document.onmousemove = this.elementDrag as any;
    }

    @bind
    @action
    private elementDrag(e: React.MouseEvent) {
        if (this.headerRef) {
            e.preventDefault();
            this.dragCount++;

            if (this.dragCount > 20) {
                // save id and width for the cells in the rows.
                this.props.vm.draggedHeaderId = cellIdToString(this.props.cell.id);
                this.props.vm.draggedHeaderWidth = this.headerRef.offsetWidth;
                // calculate the new cursor position:
                const sidebar = document.getElementById('sidebar');
                const cursorPosition = e.clientX - (sidebar?.clientWidth || 0);
                // set the element to absolute to allow movement and see through:
                const height = this.headerRef.offsetHeight;

                this.headerRef.style.top = 0 + 'px';
                this.headerRef.style.position = 'absolute';
                this.headerRef.style.zIndex = '1000';
                this.headerRef.style.height = `${height}px`;
                this.headerRef.style.opacity = '0.5';
                // calculated via cursor positon minus the elements width divided by two (for the center) then subtract sidebar.
                this.positionX = cursorPosition - this.headerRef.clientWidth / 2;
                this.headerRef.style.left = this.positionX + 'px';
                const closest = this.props.vm.stickyOffsets.sort(
                    (a, b) => Math.abs(this.positionX - a.offset) - Math.abs(this.positionX - b.offset)
                )[0];
                const closestRef = document.getElementById(cellIdToString(closest.id));
                const columnIdx = this.props.vm.stickyColumns.indexOf(cellIdToString(this.props.cell.id));
                //Decide if shadow is on the right or left
                if (this.props.cell.id != closest.id && this.dragCount > 10) {
                    if (closestRef) {
                        if (closestRef != this.savedClosestRef) {
                            if (this.savedClosestRef) this.savedClosestRef.style.boxShadow = 'none';

                            this.savedClosestRef = closestRef;
                        }

                        if (cursorPosition < closest.offset + closestRef.offsetWidth / 2) {
                            closestRef.style.boxShadow = 'inset 40px 0px 46px -30px rgba(50,70,255,1)';
                            const idx = this.props.vm.stickyColumns.indexOf(this.savedClosestRef.id);
                            this.props.vm.draggingStickyIdx = idx > columnIdx ? idx : idx + 1;
                        }

                        if (cursorPosition > closest.offset + closestRef.offsetWidth / 2) {
                            closestRef.style.boxShadow = 'inset -40px 0px 46px -30px rgba(50,70,255,1)';
                            const idx = this.props.vm.stickyColumns.indexOf(this.savedClosestRef.id);
                            this.props.vm.draggingStickyIdx = idx > columnIdx ? idx - 1 : idx;
                        }
                    }
                }
            }
        }
    }

    @bind
    @action
    private closeDragElement(e: React.MouseEvent) {
        document.onmouseup = null;
        document.onmousemove = null;

        if (this.headerRef) {
            this.headerRef.style.removeProperty('left');
            this.headerRef.style.removeProperty('opacity');
            this.headerRef.style.zIndex = '4';
            this.headerRef.style.position = 'sticky';
        }

        // Move the the item in the array if there was a close ref
        if (this.savedClosestRef && this.dragCount > 10) {
            this.savedClosestRef.style.boxShadow = 'none';
            const fromIdx = this.props.vm.stickyColumns.indexOf(cellIdToString(this.props.cell.id));
            const toIdx = this.props.vm.draggingStickyIdx;
            arrayMove(this.props.vm.stickyColumns, fromIdx, toIdx);
            this.props.vm.droppedHeaders.push(cellIdToString(this.props.cell.id));
        }

        this.updateStickyOffset();

        if (this.headerRef) {
            const offset = this.props.vm.stickyOffsets.find(c => c.id == cellIdToString(this.props.cell.id))?.offset;
            this.headerRef.style.left = offset + 'px';
        }
        this.dragCount = 0;

        this.timer = window.setTimeout(() => this.updateStickyOffset(), 1000);
    }

    @bind
    @action
    private onSortChange(sort: string[]) {
        this.props.vm.onSortChange(sort);
        if (sort.length > 0) {
            this.appStore.storage.sortedColumn = cellIdToString(this.props.cell.id);
        } else {
            this.appStore.storage.sortedColumn = '';
        }
    }

    @bind
    @action
    private bindHeaderRef(ref: HTMLTableCellElement | null) {
        this.headerRef = ref;
    }

    @bind
    @action
    private onStickyColumnChange() {
        if (this.props.rowRef && this.headerRef) {
            const { id } = this.props.cell;
            const stickyColumn = cellIdToString(id);

            this.props.vm.onStickyColumnChange(stickyColumn);
        }
    }

    @bind
    @action
    private updateStickyOffset() {
        const { vm } = this.props;

        if (this.htmlCells && this.isSticky && !this.updatingOffset) {
            this.updatingOffset = true;
            const cellId = cellIdToString(this.props.cell.id);

            let offset = 0;
            let width = 0;

            for (const cell of this.htmlCells) {
                if (cell.id == cellId) {
                    width = cell.clientWidth;
                    break;
                }

                if (vm.stickyColumns.find(c => cell.id == c)) {
                    offset = offset + cell.offsetWidth - 1;
                }
            }

            if (this.vmStickyOffset?.offset != offset) {
                if (this.vmStickyOffset) {
                    vm.stickyOffsets.splice(vm.stickyOffsets.indexOf(this.vmStickyOffset), 1);
                }

                vm.stickyOffsets.push({
                    id: cellId,
                    offset,
                    width,
                });

                this.updatingOffset = false;
                this.updateStickyOffset();
            } else {
                this.updatingOffset = false;
            }
        }
    }

    @computed
    private get htmlCells() {
        let cells: HTMLTableCellElement[] = [];
        if (this.headerRef && this.headerRef.parentNode && this.headerRef.parentNode.childNodes) {
            const rowRef = this.headerRef.parentNode as HTMLTableRowElement;
            let htmlCells = Array.from(rowRef.cells);
            const { stickyColumns } = this.props.vm;
            htmlCells = htmlCells.filter(c => c.classList.contains('sticky-header'));
            cells = htmlCells.sort((a, b) => {
                return stickyColumns.indexOf(cellIdToString(b.id)) - stickyColumns.indexOf(cellIdToString(a.id));
            });
        }

        return cells;
    }

    @computed
    private get isSticky() {
        const { id } = this.props.cell;
        const stickyColumn = cellIdToString(id);
        if (!this.props.vm.showStickyColumns) return false;

        return this.props.vm.stickyColumns.includes(stickyColumn);
    }

    @computed
    private get className() {
        let className = '';

        if (this.isSticky) {
            className = ' sticky-header';
        }
        if (this.isSorted) {
            className = className + ' sorted-header';
        }
        if (this.props.isHighlighted) {
            className = className + ' highlighted-header';
        }
        if (!this.accountStore.storage.wrappedHeaders) {
            className = className + ' no-wrap';
        }
        return className;
    }

    @computed
    private get isSorted() {
        const { cell } = this.props;
        return this.appStore.storage.sortedColumn == cellIdToString(cell.id);
    }

    @computed
    private get vmStickyOffset() {
        return this.props.vm.stickyOffsets.find(c => c.id == cellIdToString(this.props.cell.id));
    }

    @computed
    private get stickyStyle() {
        return { left: this.vmStickyOffset?.offset };
    }

    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
    @observable
    private dragCount: number = 0;
    @observable
    private savedClosestRef?: HTMLElement;
    @observable
    private positionX: number = 0;
    @observable
    private updatingOffset: boolean = false;
    @observable
    private timer?: number;
    @observable
    private shortTimer?: number;
    @observable
    private headerRef: HTMLTableCellElement | null = null;
}
